import { useState } from "react";
import { Search, Edit, Trash } from "lucide-react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/shared/Input/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EmptyPixels } from "./EmptyPixels";
import { useTranslation } from "react-i18next";
import useModalStore from "@/store/useModalStore";
import { usePixels } from "../hooks/usePixels.ts";
import { Facebook } from "@/assets/icons/Facebook";
import { GoogleAds } from "@/assets/icons/GoogleAds";
import { Tiktok } from "@/assets/icons/Tiktok";
import { Kwai } from "@/assets/icons/Kwai";
import { Taboola } from "@/assets/icons/Taboola";
import { GoogleTagManager } from "@/assets/icons/GoogleTagManager";
import { GoogleAnalytics } from "@/assets/icons/GoogleAnalytics";
import Badge from "@/components/shared/Badge/Badge";
import { Card } from "@/components/ui/card";
import Divider from "@/components/shared/Divider/Divider";
import TableListCustom from "@/components/shared/TableListCustom/TableListCustom.tsx";
import CTATable from "@/components/shared/TableListCustom/CTATable.tsx";
import { Pixels } from "@/assets/icons/Pixels.tsx";
import DialogDelete from "@/components/shared/DialogDelete/DialogDelete.tsx";
import CustomSheet from "@/components/shared/Sheet/Sheet.tsx";
import { AddPixelSheet } from "./AddPixelSheet.tsx";
import { EditPixelSheet } from "./EditPixelSheet.tsx";
import { usePermissions } from "@/auth/PermissionsContext";

interface Pixel {
  id: string;
  name: string;
  pixelProvider: string;
  pixel_id: string;
  is_enabled: boolean;
}

// Sentinela interna do EditPixelSheet (Google Ads modo conectado, campo Pixel ID
// escondido da tela) — nunca deveria ter sido persistido, mas dado antigo pode existir
// no banco. Defesa aqui: nunca exibir esse valor bruto pro seller.
const GOOGLE_ADS_PENDING_SENTINEL = "pending-google-ads-conversion-action";

/**
 * @param pixel - Linha da tabela a formatar a coluna de ID.
 * @returns Pixel ID real, ou "-" pra Google Ads sem AW-ID (conectado ou sentinela antigo).
 */
export function formatPixelIdCell(pixel: Pixel): string {
  const isGoogleAds = pixel.pixelProvider === "google_ads";
  const isEmptyOrSentinel = !pixel.pixel_id || pixel.pixel_id === GOOGLE_ADS_PENDING_SENTINEL;

  if (isGoogleAds && isEmptyOrSentinel) {
    return "-";
  }

  return pixel.pixel_id;
}

const platforms = {
  facebook: {
    name: "Facebook",
    icon: Facebook,
  },
  google_ads: {
    name: "Google Ads",
    icon: GoogleAds,
  },
  taboola: {
    name: "Taboola",
    icon: Taboola,
  },
  tiktok: {
    name: "TikTok",
    icon: Tiktok,
  },
  kwai: {
    name: "Kwai",
    icon: Kwai,
  },
  google_tag_manager: {
    name: "Google Tag Manager",
    icon: GoogleTagManager,
  },
  google_analytics: {
    name: "Google Analytics",
    icon: GoogleAnalytics,
  },
} as const;

interface PixelTableProps {
  productId: string;
}

export default function PixelTable({ productId }: PixelTableProps) {
  const { t } = useTranslation("pixels");
  const { openModal, modals, closeModal } = useModalStore();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedPixelId, setSelectedPixelId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const {
    pixels,
    isLoading,
    createPixel,
    updatePixel,
    deletePixel,
    isPending,
    isDeleting,
  } = usePixels({ productId });

  const { hasPermission } = usePermissions();
  const canCreatePixel = hasPermission('pixels.create_pixel');
  const canEditPixel = hasPermission('pixels.edit_pixel');
  const canDeletePixel = hasPermission('pixels.delete_pixel');

  const getPlatformInfo = (integrationType: string) => {
    const platform = platforms[integrationType as keyof typeof platforms];
    if (!platform) {
      return {
        name: integrationType,
        icon: null,
      };
    }
    const Icon = platform.icon;
    return {
      name: platform.name,
      icon: <Icon />,
    };
  };

  const handleEdit = (pixel: Pixel) => {
    setSelectedPixelId(pixel.id);
    openModal("edit-pixel");
  };

  const handleDeleteConfirm = () => {
    if (selectedPixelId) {
      deletePixel(selectedPixelId);
      setSelectedPixelId(null);
    }
  };

  const headers = [
    { title: t("name"), key: "name" },
    { title: t("platform"), key: "pixelProvider" },
    { title: t("id"), key: "offer" },
    { title: t("status"), key: "status" },
    { title: "", key: "actions" },
  ];

  return (
    <Card className="w-full mt-8 overflow-x-auto">
      <div className="flex md:items-center flex-col md:flex-row w-full p-4 justify-between max-md:gap-3">
        <h1 className="text-base font-semibold">{t("pixels")}</h1>
        {canCreatePixel && (
          <Button
            onClick={() => openModal("add-pixel")}
            className="text-black"
            data-cy="btn-create-pixel"
          >
            + {t("add_pixel")}
          </Button>
        )}
      </div>

      <Divider className="mt-[-5px]" />

      <>
        {pixels?.data?.length > 0 ? (
          <>
            <div className="flex items-center justify-between max-md:flex-col gap-4 mb-1 p-4">
              <Input
                icon={<Search size={15} />}
                placeholder={t("search_placeholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-[300px] max-md:w-full"
              />
              <div className="w-fit max-md:w-full">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[150px] max-md:w-full">
                    <SelectValue placeholder={t("filter_status")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("all")}</SelectItem>
                    <SelectItem value="active">{t("active")}</SelectItem>
                    <SelectItem value="inactive">{t("inactive")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <TableListCustom
              emptyDescriptionMessage={t("empty.description")}
              emptyIconMessage={<Pixels />}
              emptyTitleMessage={t("empty.title")}
              headers={headers}
              isLoadingData={isLoading}
              data={pixels}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            >
              {pixels?.data?.map((pixel: Pixel) => {
                const platformInfo = getPlatformInfo(pixel.pixelProvider);
                return (
                  <TableRow
                    key={pixel.id}
                    data-cy="table-row"
                    className="hover:bg-transparent h-[64px]"
                  >
                    <TableCell>{pixel.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {platformInfo.icon}
                        <span>{platformInfo.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{formatPixelIdCell(pixel)}</TableCell>
                    <TableCell>
                      <Badge
                        status={pixel.is_enabled ? "active" : "pixel_disabled"}
                        text={pixel.is_enabled ? undefined : t("common:inactive")}
                      />
                    </TableCell>
                    <TableCell className="w-[50px]">
                      {(canEditPixel || canDeletePixel) && (
                        <CTATable
                          items={[
                            ...(canEditPixel ? [{
                              id: 1,
                              label: t("edit"),
                              icon: <Edit />,
                              action: () => { handleEdit(pixel); },
                            }] : []),
                            ...(canDeletePixel ? [{
                              id: 2,
                              label: t("delete"),
                              icon: <Trash color="#F87171" />,
                              action: () => {
                                setSelectedPixelId(pixel.id);
                                openModal("delete-pixel");
                              },
                            }] : []),
                          ]}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableListCustom>
          </>
        ) : (
          <EmptyPixels />
        )}
      </>

      {canDeletePixel && (
        <DialogDelete
          onClose={() => closeModal("delete-pixel")}
          isOpen={modals["delete-pixel"]}
          title={t("delete_pixel_title")}
          description={t("delete_pixel_description")}
          onDelete={handleDeleteConfirm}
        />
      )}

      {canCreatePixel && (
        <CustomSheet
          open={modals["add-pixel"]}
          onClose={() => closeModal("add-pixel")}
        >
          <AddPixelSheet isLoading={isPending} onAdd={createPixel} />
        </CustomSheet>
      )}

      {canEditPixel && selectedPixelId && (
        <CustomSheet
          open={modals["edit-pixel"]}
          onClose={() => {
            closeModal("edit-pixel");
            setSelectedPixelId(null);
          }}
        >
          <EditPixelSheet
            productId={productId}
            isPending={isPending}
            pixelId={selectedPixelId}
            onUpdate={(data) => updatePixel({ pixelId: selectedPixelId, data })}
          />
        </CustomSheet>
      )}

    </Card>
  );
}
